import React, { useEffect, useMemo } from "react";
import { numberFormat } from "../../../util/araknerdlib";
import { useStat } from "../../../DataStore/StatContext";
import { useCurrency } from "../../../util/SystemVariables";
import { Skeleton, SkeletonBar } from "../../../util/skeleton/Skeleton";
import { Link } from "react-router-dom";
import FinanceCardHead from "../FinanceCardHead";
const clampPct = (n) => Math.max(0, Math.min(100, Number(n) || 0));

const Bar = ({ pct = 0, tone = "indigo" }) => {
  const width = `${clampPct(pct)}%`;

  return (
    <div className="yx-bar">
      <div
        className={`yx-bar__fill yx-bar__fill--${tone}`}
        style={{ width }}
      />
    </div>
  );
};

const FinancialPositionCard = () => {
  const {netWorth: netWorthData, loadingNetWorth } = useStat();
  const currency = useCurrency();

  const { netWorth, assetsTotal, liabsTotal, equityTotal, aPct, lPct, ePct } =
    useMemo(() => {
      const assets = Number(netWorthData?.assets) || 0;
      const liabs = Number(netWorthData?.liabilities) || 0;
      const equity = Number(netWorthData?.equity) || 0;
      const nw = Number(netWorthData?.net_worth) || assets - liabs;

      const denom = Math.max(assets + liabs + Math.abs(equity), 1);
      const ap = Math.min(100, Math.round((assets / denom) * 100));
      const lp = Math.min(100, Math.round((liabs / denom) * 100));
      const ep = Math.min(100, Math.round((Math.abs(equity) / denom) * 100));

      return {
        netWorth: nw,
        assetsTotal: assets,
        liabsTotal: liabs,
        equityTotal: equity,
        aPct: ap,
        lPct: lp,
        ePct: ep,
      };
    }, [netWorthData]);

  const isLoading = loadingNetWorth ?? !netWorthData;

  /* ---------------- Skeleton ---------------- */
  if (isLoading) {
    return (
      <div className="yx-card mb-3">
        <div className="yx-card-head">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Skeleton w="160px" h={14} />
            <Skeleton w="100px" h={12} />
          </div>

          <Skeleton w="260px" h={28} />
          <Skeleton w="160px" h={12} style={{ marginTop: 8 }} />
        </div>

        <div className="yx-card-body">
          {["Assets", "Liabilities", "Equity"].map((_, i) => (
            <div key={i} className="d-flex align-items-center mb-3" style={{ gap: 14 }}>
              <div style={{ width: 170, minWidth: 170 }}>
                <Skeleton w="130px" h={12} />
                <Skeleton w="70px" h={10} style={{ marginTop: 6 }} />
              </div>
              <div className="flex-grow-1">
                <SkeletonBar />
              </div>
            </div>
          ))}
        </div>

        <div className="yx-card-foot">
          <Skeleton w="130px" h={12} />
        </div>
      </div>
    );
  }

  return (
    <FinanceCardHead
      title="Financial position"
      right={
        <span className="yx-pill yx-pill--muted">
          As of today <i className="fa fa-chevron-down ml-1" />
        </span>
      }
      value={netWorth}
      currency={currency}
      subtitle="Business net worth"
      negativeParen={true}
    >
      {/* Rows */}
      <div className="d-flex flex-column" style={{ gap: 14 }}>
        {/* Assets */}
        <div className="d-flex align-items-center" style={{ gap: 14 }}>
          <div style={{ width: 170, minWidth: 170 }}>
            <div className="yx-row">
              {currency} {numberFormat(assetsTotal)}
            </div>
            <div className="yx-sub">Assets</div>
          </div>
          <Bar pct={aPct} tone="indigo" />
        </div>

        {/* Liabilities */}
        <div className="d-flex align-items-center" style={{ gap: 14 }}>
          <div style={{ width: 170, minWidth: 170 }}>
            <div className="yx-row">
              {currency} {numberFormat(liabsTotal)}
            </div>
            <div className="yx-sub">Liabilities</div>
          </div>
          <Bar pct={lPct} tone="muted" />
        </div>

        {/* Equity */}
        <div className="d-flex align-items-center" style={{ gap: 14 }}>
          <div style={{ width: 170, minWidth: 170 }}>
            <div className={`yx-row ${equityTotal < 0 ? "text-danger" : ""}`}>
              {currency} {numberFormat(equityTotal)}
            </div>
            <div className="yx-sub">Equity</div>
          </div>
          <Bar pct={ePct} tone="soft" />
        </div>

        <div className="yx-divider" />

        <Link to="/report-balance-sheet" className="yx-link d-inline-flex align-items-center">
          See balance sheet <i className="fa fa-arrow-right ml-2" />
        </Link>
      </div>
    </FinanceCardHead>
  );
};

export default FinancialPositionCard;
